import { useEffect, useRef, useState } from "react";

const INVALID_BASELINE = Object.freeze({ sentinel: "invalid" });
const LEGACY_NULL_BASELINE = Object.freeze({
  mode: "legacy",
  contact_sources: Object.freeze([]),
  tags: Object.freeze([]),
});
const INITIAL_CUSTOM_SOURCE = "fractolabs_form";
const INVALID_MESSAGE =
  "Saved start rules are invalid. Automatic start is disabled until rules are replaced.";
const SAVED_EMPTY_STATUS = "Automatic start for new contacts is disabled.";
const UNSAVED_EMPTY_STATUS = "Automatic start will be disabled after saving.";
const UNSAVED_CHANGES = "Unsaved changes";
const MATCH_HINT =
  "The bot starts when the contact source or at least one contact tag matches.";
const COMPARE_HINT =
  "Comparison is exact and ignores letter case and spaces at the ends.";
const WIDGET_HINT =
  "Widget contacts are handled by the widget conversation and are excluded.";
const V2_HINT =
  "These rules apply to Contact Created webhooks. The Roofing V2 GHL channel is currently unavailable.";
const UNKNOWN_MESSAGE =
  "Save could not be confirmed. Retry saving or reload the page to check the saved rules.";

function isCustomSnapshot(savedRules, savedRulesState) {
  return (
    savedRulesState === "custom" &&
    savedRules?.mode === "custom" &&
    Array.isArray(savedRules.contact_sources) &&
    Array.isArray(savedRules.tags)
  );
}

function draftRows(rules, state, key, idRef) {
  if (!isCustomSnapshot(rules, state)) return [];
  return rules[key].map((value) => {
    const id = idRef.current;
    idRef.current += 1;
    return { id, value };
  });
}

function sameList(left, right) {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
}

function uiBaseline(savedRules, savedRulesState) {
  if (savedRulesState === "invalid") return INVALID_BASELINE;
  if (savedRulesState === "legacy" && savedRules == null) {
    return LEGACY_NULL_BASELINE;
  }
  if (isCustomSnapshot(savedRules, savedRulesState)) {
    return {
      mode: "custom",
      contact_sources: savedRules.contact_sources,
      tags: savedRules.tags,
    };
  }
  if (savedRulesState === "legacy") return LEGACY_NULL_BASELINE;
  return INVALID_BASELINE;
}

function snapshotFields(nextRules, nextState, idRef) {
  return {
    baseline: uiBaseline(nextRules, nextState),
    mode: isCustomSnapshot(nextRules, nextState) ? "custom" : "legacy",
    sources: draftRows(nextRules, nextState, "contact_sources", idRef),
    tags: draftRows(nextRules, nextState, "tags", idRef),
  };
}

function serializableDraft(mode, sourceRows, tagRows) {
  if (mode === "legacy") {
    return { mode: "legacy", contact_sources: [], tags: [] };
  }
  return {
    mode: "custom",
    contact_sources: sourceRows.map((row) => row.value),
    tags: tagRows.map((row) => row.value),
  };
}

function sameDraft(draft, baseline) {
  if (!baseline || baseline.sentinel === "invalid") return false;
  return (
    draft.mode === baseline.mode &&
    sameList(draft.contact_sources, baseline.contact_sources) &&
    sameList(draft.tags, baseline.tags)
  );
}

function sameBaseline(left, right) {
  if (left === right) return true;
  const leftInvalid = !left || left.sentinel === "invalid";
  const rightInvalid = !right || right.sentinel === "invalid";
  if (leftInvalid || rightInvalid) return leftInvalid && rightInvalid;
  return sameDraft(left, right);
}

function DraftRuleList({
  heading,
  itemLabel,
  rows,
  locked,
  testId,
  onAdd,
  onRemove,
  onChange,
}) {
  const labelId = `${testId}-label`;
  return (
    <div data-testid={testId}>
      <h3
        id={labelId}
        className="font-heading font-semibold text-sm text-[#F3F4F6] mb-2"
      >
        {heading}
      </h3>
      {rows.length > 0 ? (
        <ul className="space-y-2">
          {rows.map((row, index) => {
            const inputId = `${testId}-${row.id}`;
            const fieldLabel = `${itemLabel} ${index + 1}`;
            return (
              <li key={row.id} className="flex items-end gap-2">
                <div className="flex-1">
                  <label
                    htmlFor={inputId}
                    className="block text-[#9CA3AF] font-body text-xs mb-1"
                  >
                    {fieldLabel}
                  </label>
                  <input
                    id={inputId}
                    type="text"
                    value={row.value}
                    disabled={locked}
                    onChange={(event) => onChange(row.id, event.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-white/10 bg-transparent text-[#F3F4F6] font-body text-sm"
                  />
                </div>
                <button
                  type="button"
                  disabled={locked}
                  onClick={() => onRemove(row.id)}
                  className="px-4 py-2.5 rounded-lg border border-white/10 text-[#9CA3AF] font-body text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {`Remove ${fieldLabel}`}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      <button
        type="button"
        disabled={locked}
        onClick={onAdd}
        className="px-4 py-2.5 rounded-lg border border-white/10 text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-white/20 transition-all font-body text-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {`Add ${itemLabel}`}
      </button>
    </div>
  );
}

export function GhlDialogStartRules({
  integrationId,
  savedRules,
  savedRulesState,
  isLoading,
  loadError,
  isSaving,
  saveError,
  saveOutcomeUnknown,
  success,
  onSave,
  onRetryLoad,
  onDirtyChange,
}) {
  const nextId = useRef(1);
  const [seed] = useState(() =>
    snapshotFields(savedRules, savedRulesState, nextId)
  );
  const [acceptedBaseline, setAcceptedBaseline] = useState(seed.baseline);
  const [sources, setSources] = useState(seed.sources);
  const [tags, setTags] = useState(seed.tags);
  const [mode, setMode] = useState(seed.mode);
  const [customCache, setCustomCache] = useState(null);
  const [repairMode, setRepairMode] = useState(null);
  const [hideSuccess, setHideSuccess] = useState(false);
  const baseline = acceptedBaseline;

  function applyEditorSnapshot(nextRules, nextState) {
    const next = snapshotFields(nextRules, nextState, nextId);
    setAcceptedBaseline(next.baseline);
    setMode(next.mode);
    setSources(next.sources);
    setTags(next.tags);
    setCustomCache(null);
    setRepairMode(null);
  }
  const readOnly = onSave == null;
  const showSavedLists = !isLoading && !loadError;
  const invalid = baseline === INVALID_BASELINE;
  const repaired = invalid && repairMode != null;
  const showMode = showSavedLists && (!invalid || repaired);
  const showEditor = showMode && mode === "custom";
  const locked = !showSavedLists || isSaving || readOnly;
  const hasBlank =
    mode === "custom" &&
    (sources.some((row) => row.value === "") ||
      tags.some((row) => row.value === ""));
  const dirty = !sameDraft(
    serializableDraft(mode, sources, tags),
    baseline
  );
  const saveEnabled =
    showMode && !locked && !hasBlank && (dirty || saveOutcomeUnknown);
  const pendingSave = useRef(false);
  const successAtPending = useRef(success);
  const pendingEpoch = useRef(0);
  const editEpoch = useRef(0);
  const cleanNoticeEpoch = useRef(null);
  const integrationRef = useRef(integrationId);
  const notifiedIntegration = useRef(integrationId);
  const onDirtyChangeRef = useRef(onDirtyChange);
  const seenSuccess = useRef(success);
  onDirtyChangeRef.current = onDirtyChange;
  if (success !== seenSuccess.current) {
    seenSuccess.current = success;
    if (success) setHideSuccess(false);
  }
  const candidateBaseline = uiBaseline(savedRules, savedRulesState);
  const wasClean =
    !isSaving &&
    !saveOutcomeUnknown &&
    (baseline.sentinel === "invalid"
      ? repairMode == null
      : sameDraft(serializableDraft(mode, sources, tags), baseline));
  if (integrationRef.current !== integrationId) {
    integrationRef.current = integrationId;
    pendingSave.current = false;
    setHideSuccess(false);
    applyEditorSnapshot(savedRules, savedRulesState);
  } else if (wasClean && !sameBaseline(candidateBaseline, baseline)) {
    cleanNoticeEpoch.current = editEpoch.current;
    applyEditorSnapshot(savedRules, savedRulesState);
  }
  const savedCustomEmpty =
    !invalid &&
    !dirty &&
    baseline.mode === "custom" &&
    baseline.contact_sources.length === 0 &&
    baseline.tags.length === 0;
  const unsavedCustomEmpty =
    showMode &&
    dirty &&
    mode === "custom" &&
    sources.length === 0 &&
    tags.length === 0;

  function createRow(value) {
    const id = nextId.current;
    nextId.current += 1;
    return { id, value };
  }

  function dismissSuccess() {
    if (success) setHideSuccess(true);
  }

  function reportDirty(nextMode, nextSources, nextTags) {
    if (typeof onDirtyChange !== "function") return;
    const nextDirty = !sameDraft(
      serializableDraft(nextMode, nextSources, nextTags),
      baseline
    );
    if (nextDirty) editEpoch.current += 1;
    onDirtyChange(nextDirty);
  }

  useEffect(() => {
    if (!pendingSave.current || isSaving) return;
    if (saveError || (saveOutcomeUnknown && !success)) {
      pendingSave.current = false;
      return;
    }
    if (!success || success === successAtPending.current || saveOutcomeUnknown) {
      return;
    }
    if (pendingEpoch.current !== editEpoch.current) {
      pendingSave.current = false;
      return;
    }
    const epoch = editEpoch.current;
    pendingSave.current = false;
    successAtPending.current = success;
    applyEditorSnapshot(savedRules, savedRulesState);
    if (epoch !== editEpoch.current) return;
    const notify = onDirtyChangeRef.current;
    if (typeof notify === "function") notify(false);
  }, [
    success,
    isSaving,
    saveError,
    saveOutcomeUnknown,
    savedRules,
    savedRulesState,
  ]);

  useEffect(() => {
    if (notifiedIntegration.current === integrationId) return;
    notifiedIntegration.current = integrationId;
    const notify = onDirtyChangeRef.current;
    if (typeof notify === "function") notify(false);
  }, [integrationId]);

  useEffect(() => {
    if (cleanNoticeEpoch.current == null) return;
    const epoch = cleanNoticeEpoch.current;
    cleanNoticeEpoch.current = null;
    if (epoch !== editEpoch.current) return;
    const notify = onDirtyChangeRef.current;
    if (typeof notify === "function") notify(false);
  });

  function changeSources(id, value) {
    if (locked) return;
    dismissSuccess();
    const next = sources.map((row) =>
      row.id === id ? { ...row, value } : row
    );
    setSources(next);
    reportDirty(mode, next, tags);
  }

  function changeTags(id, value) {
    if (locked) return;
    dismissSuccess();
    const next = tags.map((row) => (row.id === id ? { ...row, value } : row));
    setTags(next);
    reportDirty(mode, sources, next);
  }

  function addSource() {
    if (locked) return;
    dismissSuccess();
    const next = [...sources, createRow("")];
    setSources(next);
    reportDirty(mode, next, tags);
  }

  function addTag() {
    if (locked) return;
    dismissSuccess();
    const next = [...tags, createRow("")];
    setTags(next);
    reportDirty(mode, sources, next);
  }

  function removeSource(id) {
    if (locked) return;
    dismissSuccess();
    const next = sources.filter((row) => row.id !== id);
    setSources(next);
    reportDirty(mode, next, tags);
  }

  function removeTag(id) {
    if (locked) return;
    dismissSuccess();
    const next = tags.filter((row) => row.id !== id);
    setTags(next);
    reportDirty(mode, sources, next);
  }

  function switchMode(nextMode) {
    if (locked || (invalid && !repaired) || nextMode === mode) return;
    dismissSuccess();
    let nextSources = sources;
    let nextTags = tags;
    if (mode === "custom" && nextMode === "legacy") {
      setCustomCache({ sources, tags });
    }
    if (nextMode === "custom") {
      if (customCache) {
        nextSources = customCache.sources;
        nextTags = customCache.tags;
      } else {
        nextSources = [createRow(INITIAL_CUSTOM_SOURCE)];
        nextTags = [];
      }
      setSources(nextSources);
      setTags(nextTags);
    }
    setMode(nextMode);
    reportDirty(nextMode, nextSources, nextTags);
  }

  function chooseRepair(nextMode) {
    if (locked || !invalid || repairMode != null) return;
    dismissSuccess();
    const nextSources =
      nextMode === "custom" ? [createRow(INITIAL_CUSTOM_SOURCE)] : [];
    const nextTags = [];
    setRepairMode(nextMode);
    setMode(nextMode);
    setSources(nextSources);
    setTags(nextTags);
    setCustomCache(null);
    reportDirty(nextMode, nextSources, nextTags);
  }

  function handleRetryLoad() {
    if (savedRulesState || saveOutcomeUnknown) return;
    if (typeof onRetryLoad === "function") {
      onRetryLoad();
    }
  }

  function handleSave() {
    if (!saveEnabled || typeof onSave !== "function") return;
    pendingSave.current = true;
    successAtPending.current = success;
    pendingEpoch.current = editEpoch.current;
    onSave(serializableDraft(mode, sources, tags));
  }

  return (
    <section
      className="glass rounded-2xl p-6 border border-white/10 mb-6 space-y-4"
      data-testid="ghl-dialog-start-rules"
      data-integration-id={integrationId ?? ""}
      data-rules-state={savedRulesState ?? ""}
      data-saving={isSaving ? "true" : "false"}
      data-readonly={readOnly ? "true" : "false"}
      data-dirty-reporter={
        typeof onDirtyChange === "function" ? "ready" : "missing"
      }
      data-outcome-unknown={saveOutcomeUnknown ? "true" : "false"}
      data-baseline={invalid ? "invalid" : baseline.mode}
    >
      <h2 className="font-heading font-bold text-lg text-[#F3F4F6]">
        Conversation start rules
      </h2>
      {isLoading ? (
        <p role="status" className="text-[#9CA3AF] font-body text-sm">
          Loading start rules
        </p>
      ) : null}
      {!isLoading && loadError ? (
        <div role="alert" className="space-y-3">
          <p className="text-red-400 font-body text-sm">{loadError}</p>
          {!savedRulesState && !saveOutcomeUnknown ? (
            <button
              type="button"
              onClick={handleRetryLoad}
              className="px-4 py-2.5 rounded-lg border border-white/10 text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-white/20 transition-all font-body text-sm"
            >
              Retry loading
            </button>
          ) : null}
        </div>
      ) : null}
      {showMode ? (
        <fieldset disabled={locked} className="space-y-2">
          <legend className="sr-only">Start rule mode</legend>
          <label className="flex items-center gap-2 text-[#F3F4F6] font-body text-sm">
            <input
              type="radio"
              name={`ghl-dialog-start-mode-${integrationId || "new"}`}
              value="legacy"
              checked={mode === "legacy"}
              disabled={locked}
              onChange={() => switchMode("legacy")}
            />
            Default rules
          </label>
          <label className="flex items-center gap-2 text-[#F3F4F6] font-body text-sm">
            <input
              type="radio"
              name={`ghl-dialog-start-mode-${integrationId || "new"}`}
              value="custom"
              checked={mode === "custom"}
              disabled={locked}
              onChange={() => switchMode("custom")}
            />
            Custom rules
          </label>
        </fieldset>
      ) : null}
      {showMode && mode === "legacy" ? (
        <p className="text-[#9CA3AF] font-body text-sm">
          Default rules start a dialog for source fractolabs_form or
          attribution medium form. Widget contacts are excluded.
        </p>
      ) : null}
      {showMode && mode === "custom" ? (
        <div className="space-y-2">
          <p className="text-[#9CA3AF] font-body text-sm">{MATCH_HINT}</p>
          <p className="text-[#9CA3AF] font-body text-sm">{COMPARE_HINT}</p>
          <p className="text-[#9CA3AF] font-body text-sm">
            Attribution medium form no longer starts a dialog by itself.
          </p>
        </div>
      ) : null}
      {showSavedLists ? (
        <p className="text-[#9CA3AF] font-body text-sm">{WIDGET_HINT}</p>
      ) : null}
      {showEditor ? (
        <>
          <DraftRuleList
            heading="Contact sources"
            itemLabel="contact source"
            rows={sources}
            locked={locked}
            testId="ghl-dialog-start-sources"
            onAdd={addSource}
            onRemove={removeSource}
            onChange={changeSources}
          />
          <DraftRuleList
            heading="Contact tags"
            itemLabel="contact tag"
            rows={tags}
            locked={locked}
            testId="ghl-dialog-start-tags"
            onAdd={addTag}
            onRemove={removeTag}
            onChange={changeTags}
          />
        </>
      ) : null}
      {invalid && showSavedLists ? (
        <div className="space-y-3">
          <p role="status" className="text-red-400 font-body text-sm">
            {INVALID_MESSAGE}
          </p>
          {repairMode == null ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={locked}
                onClick={() => chooseRepair("legacy")}
                className="px-4 py-2.5 rounded-lg border border-white/10 text-[#9CA3AF] font-body text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Use default rules
              </button>
              <button
                type="button"
                disabled={locked}
                onClick={() => chooseRepair("custom")}
                className="px-4 py-2.5 rounded-lg border border-white/10 text-[#9CA3AF] font-body text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Set custom rules
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
      {savedCustomEmpty ? (
        <p className="text-[#9CA3AF] font-body text-sm">{SAVED_EMPTY_STATUS}</p>
      ) : null}
      {unsavedCustomEmpty ? (
        <p className="text-[#9CA3AF] font-body text-sm">{UNSAVED_EMPTY_STATUS}</p>
      ) : null}
      {dirty && showMode ? (
        <p className="text-[#9CA3AF] font-body text-sm">{UNSAVED_CHANGES}</p>
      ) : null}
      {success && !hideSuccess ? (
        <p role="status" className="text-[#9CA3AF] font-body text-sm">
          {success}
        </p>
      ) : null}
      {saveOutcomeUnknown ? (
        <p
          data-testid="ghl-dialog-start-unknown"
          className="text-[#9CA3AF] font-body text-sm"
        >
          {UNKNOWN_MESSAGE}
        </p>
      ) : null}
      {saveError ? (
        <p
          role="alert"
          data-testid="ghl-dialog-start-save-error"
          className="text-red-400 font-body text-sm"
        >
          {saveError}
        </p>
      ) : null}
      <button
        type="button"
        disabled={!saveEnabled}
        onClick={handleSave}
        data-testid="ghl-dialog-start-save"
        className="px-4 py-2.5 rounded-lg border border-white/10 text-[#9CA3AF] font-body text-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Save start rules
      </button>
      <p className="text-[#9CA3AF] font-body text-sm">{V2_HINT}</p>
    </section>
  );
}
